import clsx from 'clsx';
import { useEffect, useState, type CSSProperties } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { BillingCountdown } from './BillingCountdown';
import { ActiveBulkRun } from './ActiveBulkRun';
import { ErrorBoundary } from '../ErrorBoundary';
import { UniversalSearch } from './UniversalSearch';
import { TaskToasts } from './TaskToasts';
import { TasksMenu } from './TasksMenu';
import { NotificationsBell } from './NotificationsBell';
import { SHEET_PANEL } from './panel';
import { ROLE_LABELS, VERSION, type SessionOrg } from '@simpleacs/shared';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ExternalLink,
  LogOut,
  Menu,
  Moon,
  Search,
  Sun,
  X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { DEFAULT_BRAND, useBranding } from '../../lib/branding';
import { useTheme } from '../../lib/theme';
import { accentForPath, DEFAULT_ACCENT, NAV_PRIMARY, NAV_SECTIONS, sectionForPath } from './nav';
import { platformWallClock } from '../../lib/format';

function OrgSwitcher() {
  const { session, activeOrg, switchOrg } = useSession();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SessionOrg[] | null>(null);
  const [searching, setSearching] = useState(false);

  const isSuperadmin = session?.user.role === 'superadmin';
  const term = search.trim();

  // Only reach for the server once the operator actually types; the session
  // already carries the handful they most likely want.
  useEffect(() => {
    if (!open || !isSuperadmin || term.length === 0) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const found = await api.get<{ items: SessionOrg[] }>('/orgs/lookup', {
          search: term,
          limit: 20,
        });
        if (!cancelled) setResults(found.items);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, isSuperadmin, term]);

  if (!session) return null;

  // Org-scoped users have exactly one org and nothing to switch between.
  if (!isSuperadmin) {
    return (
      <span className="hidden items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-xs text-muted sm:inline-flex">
        <Building2 className="size-3.5" />
        {activeOrg?.name ?? '—'}
      </span>
    );
  }

  const shown = results ?? session.orgs;

  const choose = async (orgId: string | null) => {
    await switchOrg(orgId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-xs text-ink hover:bg-surface-3"
      >
        <Building2 className="size-3.5 shrink-0 text-muted" />
        {/*
          The label is what makes this 146px wide, and on a phone that is the
          difference between the bar fitting and the bell being pushed off the
          screen. The icon still says what the control is, and the sheet that
          opens names the organization at the top.
        */}
        <span className="hidden max-w-[10rem] truncate sm:inline">
          {activeOrg?.name ?? 'All organizations'}
        </span>
        <ChevronDown className="size-3.5 shrink-0 text-muted" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
              setSearch('');
            }}
          />
          <div
            className={`z-20 overflow-hidden rounded-xl border border-line bg-surface shadow-xl ${SHEET_PANEL} sm:top-auto sm:mt-2 sm:w-72`}
          >
            <div className="border-b border-line p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search organizations…"
                  autoFocus
                  className="w-full rounded-lg border border-line bg-surface-2 py-1.5 pl-8 pr-2 text-xs text-ink placeholder:text-faint focus:border-primary focus:outline-none"
                />
              </div>
            </div>

            <button
              className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-ink hover:bg-surface-2"
              onClick={() => void choose(null)}
            >
              All organizations
              {!activeOrg && <Check className="size-4 text-primary" />}
            </button>
            <div className="h-px bg-line" />

            <div className="max-h-72 overflow-y-auto">
              {searching && shown.length === 0 ? (
                <p className="px-4 py-3 text-xs text-faint">Searching…</p>
              ) : shown.length === 0 ? (
                <p className="px-4 py-3 text-xs text-faint">No organization matches “{term}”.</p>
              ) : (
                shown.map((org) => (
                  <button
                    key={org.id}
                    className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-ink hover:bg-surface-2"
                    onClick={() => void choose(org.id)}
                  >
                    <span className="min-w-0 truncate">
                      {org.name}
                      <span className="ml-2 font-mono text-xs text-faint">{org.slug}</span>
                    </span>
                    {activeOrg?.id === org.id && <Check className="size-4 shrink-0 text-primary" />}
                  </button>
                ))
              )}
            </div>

            {!term && (
              <p className="border-t border-line px-4 py-2 text-[11px] text-faint">
                Showing the {session.orgs.length} most recent — search for the rest.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Whether the menu shows its sections folded.
 *
 * Persisted, because it is a statement about how somebody likes to work rather
 * than about the page they are on — an operator who opened the whole menu once
 * should not have to open it again tomorrow. Read defensively: a private window
 * throws on localStorage, and a menu that fails to render is worse than a menu
 * that forgets a preference.
 */
const MENU_MODE_KEY = 'simpleacs.menu.folded';

function readMenuFolded(): boolean {
  try {
    return window.localStorage.getItem(MENU_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const [folded, setFolded] = useState(readMenuFolded);
  /** Sections the operator has opened by hand, in folded mode. */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const currentSection = sectionForPath(pathname);

  const { session, logout, can } = useSession();
  const brand = useBranding().data?.effective;
  if (!session) return null;
  const isSuperadmin = session.user.role === 'superadmin';

  return (
    <div className="flex h-full flex-col border-r border-line bg-surface">
      <div className="flex items-center gap-3 border-b border-line px-5 py-4">
        <img
          src={brand?.logoDataUri || DEFAULT_BRAND.logo}
          alt=""
          className="size-9 shrink-0 object-contain"
        />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">
            {brand?.displayName || DEFAULT_BRAND.displayName}
          </div>
          <div className="truncate text-[10px] uppercase tracking-wider text-faint">
            {brand?.tagline || DEFAULT_BRAND.tagline}
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {/*
          Outside the section list on purpose — no header, no fold, always in
          the same place. See NAV_PRIMARY.
        */}
        <NavLink
          to={NAV_PRIMARY.to}
          end
          onClick={onNavigate}
          style={({ isActive }) =>
            isActive
              ? {
                  backgroundColor: `color-mix(in srgb, ${DEFAULT_ACCENT} 15%, transparent)`,
                  color: DEFAULT_ACCENT,
                }
              : undefined
          }
          className={({ isActive }) =>
            clsx(
              'mt-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              isActive ? 'font-medium' : 'text-muted hover:bg-surface-2 hover:text-ink',
            )
          }
        >
          <NAV_PRIMARY.icon className="size-4" />
          {NAV_PRIMARY.label}
        </NavLink>

        {NAV_SECTIONS.map((section) => {
          /*
            The section holding the current page is ALWAYS open, whatever the
            operator has folded away. A menu that highlights an item inside a
            section it has closed is a menu lying about where you are — and the
            highlight is the whole reason the section matters.
          */
          const isCurrent = section.label === currentSection?.label;
          const open = !folded || isCurrent || expanded.has(section.label);
          const items = section.items.filter((item) => {
            if (item.superadminOnly) return isSuperadmin;
            return item.capability ? can(item.capability) : true;
          });
          if (items.length === 0) return null;

          return (
            <div key={section.label}>
              {folded ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((previous) => {
                      const next = new Set(previous);
                      if (next.has(section.label)) next.delete(section.label);
                      else next.add(section.label);
                      return next;
                    })
                  }
                  /*
                    The current section's header is not a toggle: closing it
                    would hide the page you are on. It still renders as a
                    header so the menu does not change shape as you navigate.
                  */
                  disabled={isCurrent}
                  className="section-label flex w-full items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-left hover:bg-surface-2 disabled:cursor-default disabled:hover:bg-transparent"
                  style={{ color: section.accent }}
                  aria-expanded={open}
                >
                  <span>{section.label}</span>
                  <ChevronRight
                    className={clsx(
                      'size-3.5 shrink-0 transition-transform',
                      open && 'rotate-90',
                    )}
                  />
                </button>
              ) : (
                <div className="section-label" style={{ color: section.accent }}>
                  {section.label}
                </div>
              )}
              {open &&
                items.map((item) => {
                const Icon = item.icon;
                if (item.comingSoon) {
                  return (
                    <div
                      key={item.to}
                      className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm text-faint/70"
                      title="Coming in a later phase"
                    >
                      <Icon className="size-4" />
                      <span className="flex-1">{item.label}</span>
                      <span className="rounded border border-line px-1 text-[9px] uppercase tracking-wide">
                        soon
                      </span>
                    </div>
                  );
                }

                if (item.external) {
                  return (
                    <a
                      key={item.to}
                      href={item.to}
                      target="_blank"
                      rel="noreferrer"
                      onClick={onNavigate}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-ink"
                    >
                      <Icon className="size-4" />
                      <span className="flex-1">{item.label}</span>
                      <ExternalLink className="size-3.5 text-faint" />
                    </a>
                  );
                }

                const children = (item.children ?? []).filter((child) =>
                  child.capability ? can(child.capability) : true,
                );

                return (
                  <div key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.exact}
                      onClick={onNavigate}
                      style={({ isActive }) =>
                        isActive
                          ? {
                              backgroundColor: `color-mix(in srgb, ${section.accent} 15%, transparent)`,
                              color: section.accent,
                            }
                          : undefined
                      }
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                          isActive ? 'font-medium' : 'text-muted hover:bg-surface-2 hover:text-ink',
                        )
                      }
                    >
                      <Icon className="size-4" />
                      {item.label}
                    </NavLink>
                    {children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={onNavigate}
                          style={({ isActive }) =>
                            isActive
                              ? {
                                  backgroundColor: `color-mix(in srgb, ${section.accent} 15%, transparent)`,
                                  color: section.accent,
                                }
                              : undefined
                          }
                          className={({ isActive }) =>
                            clsx(
                              'ml-6 flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors',
                              isActive ? 'font-medium' : 'text-muted hover:bg-surface-2 hover:text-ink',
                            )
                          }
                        >
                          <ChildIcon className="size-3.5" />
                          {child.label}
                        </NavLink>
                      );
                    })}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </nav>

      {/*
        The switch, at the foot of the menu where the operator's own
        preferences live rather than in Settings: it is about this menu, it is
        changed while looking at this menu, and a preference you have to go
        somewhere else to set is one nobody finds.
      */}
      <button
        type="button"
        onClick={() => {
          const next = !folded;
          setFolded(next);
          try {
            window.localStorage.setItem(MENU_MODE_KEY, next ? '1' : '0');
          } catch {
            // A private window forgets the choice; the menu still works.
          }
        }}
        className="flex items-center justify-between gap-2 border-t border-line px-4 py-2 text-xs text-muted hover:bg-surface-2 hover:text-ink"
        title={folded ? 'Show every section at once' : 'Fold sections away until you open them'}
      >
        <span className="flex items-center gap-2">
          {folded ? <ChevronsUpDown className="size-3.5" /> : <ChevronsDownUp className="size-3.5" />}
          {folded ? 'Expand the menu' : 'Fold the menu'}
        </span>
      </button>

      <div className="flex items-center gap-3 border-t border-line px-4 py-3">
        <Link
          to="/profile"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 -m-1 hover:bg-surface-2"
          title="Your profile"
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
            {session.user.name.slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-ink">{session.user.name}</div>
            <div className="truncate text-xs text-faint">{ROLE_LABELS[session.user.role]}</div>
          </div>
        </Link>
        <button
          onClick={() => void logout()}
          title="Sign out"
          className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-danger"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </div>
  );
}

function pageTitle(pathname: string): string {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)) return item.label;
    }
  }
  return 'SimpleACS';
}

export function AppShell() {
  const { session } = useSession();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const footerBrand = useBranding().data?.effective;

  return (
    <div className="flex h-full bg-canvas">
      {/* `print:hidden` on the three shell regions: Ctrl+P on an invoice should
          put the invoice on the paper, not the navigation around it. The
          `@media print` block in index.css does the rest (light palette, page
          breaks) — this marks what simply should not be there. */}
      <aside className="print:hidden hidden w-60 shrink-0 lg:block">
        <Sidebar />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="print:hidden flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              className="rounded-lg p-2 text-muted hover:bg-surface-2 lg:hidden"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="size-4" /> : <Menu className="size-4" />}
            </button>
            <span className="text-sm font-medium text-ink">{pageTitle(location.pathname)}</span>
          </div>

          <div className="flex items-center gap-2">
            <UniversalSearch />
            <OrgSwitcher />
            <TasksMenu />
            <NotificationsBell />
            <button
              onClick={toggle}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-ink"
            >
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            {/*
              Hidden on a phone: it is 69px of text restating something the
              signed-in operator already knows, and it was costing the bell its
              place in the bar. It returns at the sm breakpoint, where there is
              room for it.
            */}
            <span className="hidden items-center gap-1.5 rounded-full border border-success/30 bg-success-soft px-2.5 py-1 text-xs font-medium text-success sm:inline-flex">
              <span className="size-1.5 rounded-full bg-current" />
              {session ? ROLE_LABELS[session.user.role] : ''}
            </span>
          </div>
        </header>

        {/* The run in flight, pinned under the header on EVERY page — a fleet
            operation must not become invisible because a tab was closed. */}
        {/* Wrapped only so one `print:hidden` covers both. The banner is a
            full-width block either way and the toasts are fixed-position, so
            the extra element costs the on-screen layout nothing. */}
        <div className="print:hidden">
          <ActiveBulkRun />
          <TaskToasts />
        </div>

        <main
          className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6"
          style={{ '--section-accent': accentForPath(location.pathname) } as CSSProperties}
        >
          {/* Above every page, not just billing: the point is that somebody
              working on ONUs sees the date coming. */}
          <BillingCountdown />
          {/* Around the PAGE and nothing else. A page that throws while
              drawing — the bulk page's update loop of 2026-09-13 — costs the
              page: the sidebar, the header, the run banner and the toasts stay
              up, and the notice offers Try again. Reset on the pathname, so
              walking to another page draws that page. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>

        <footer className="print:hidden flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-line bg-surface px-4 py-2.5 text-center text-xs text-muted">
          <span>Copyright {platformWallClock(Date.now())?.year}</span>
          <span className="text-faint">|</span>
          <a
            href="/changelog"
            target="_blank"
            rel="noreferrer"
            className="font-mono text-link hover:underline"
          >
            v{VERSION}
          </a>
          <span className="text-faint">|</span>
          {/* An operator who has branded the platform gets their own line here.
              Left alone, it stays the product's own attribution. */}
          {footerBrand?.footerNote ? (
            <span>{footerBrand.footerNote}</span>
          ) : (
            <span>
              <a
                href="https://acs.simplux.africa"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-link hover:underline"
              >
                SimpleACS
              </a>{' '}
              developed by{' '}
              <a
                href="https://simplux.africa"
                target="_blank"
                rel="noreferrer"
                className="text-link hover:underline"
              >
                Simplux Africa
              </a>
            </span>
          )}
          {footerBrand?.supportEmail && (
            <>
              <span className="text-faint">|</span>
              <a href={`mailto:${footerBrand.supportEmail}`} className="text-link hover:underline">
                {footerBrand.supportEmail}
              </a>
            </>
          )}
          {footerBrand?.supportUrl && (
            <>
              <span className="text-faint">|</span>
              <a
                href={footerBrand.supportUrl}
                target="_blank"
                rel="noreferrer"
                className="text-link hover:underline"
              >
                Support
              </a>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
